import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { modalStore } from 'src/mobx/modalStore';
import { BRANCHES_MODAL_FETCH_PARAMETER, IBranch } from 'src/types/branches';
import { getUnavailable } from 'src/utils/helpers';
import { defaultPagination, PER_PAGE } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import { userStore } from '~/mobx/userStore';
import client from '~/services/client';
import logger from '~/services/logger';
import { IPagination, MODALS_CRUMB, TBooleanInNumber } from '~/types/main';
import { arrayMerge } from '~/utils/formatter';
import { IAskAgainModalSubmit } from '~/view/containers/Modals/AskAgainModal/AskAgainModal';
import { BranchesModalProfile, IBranchesModalItem } from '~/view/containers/Modals/BranchesModal/BranchesModal';
import { checkBranchesPosConflict, getBranchesWithoutPosConflict } from '~/view/containers/Modals/BranchesModal/helpers';

class BranchesModalStore {
  constructor() {
    makeObservable(this);
  }

  @observable loading: boolean = false;

  @observable selectedBranches: IBranchesModalItem[] = [];
  @observable selectedUnavailableBranches: IBranchesModalItem[] = [];
  @observable askAgainBranches: IBranch[] = [];

  @observable searchValue: string = '';
  @observable activeCrumb: MODALS_CRUMB = MODALS_CRUMB.ALL;
  @observable pagination: IPagination<IBranch> = defaultPagination;

  @computed get isMulti(): boolean {
    return Boolean(modalStore.branchesModal?.isMulti);
  }

  @computed get isBusy(): boolean {
    return Boolean(modalStore.branchesModal?.busy);
  }

  @computed get isHighlightUnavailable(): boolean {
    return Boolean(modalStore.branchesModal?.highlightUnavailable);
  }

  @computed get isSelectedCrumb(): boolean {
    return this.activeCrumb === MODALS_CRUMB.SELECTED;
  }

  @computed get isSubmitDisable(): boolean {
    return modalStore.branchesModal?.saveWithoutBranches ? false : Boolean(!this.selectedBranches.length);
  }

  @computed get modalTitle(): string {
    if (modalStore.branchesModal?.headerTitle) return modalStore.branchesModal?.headerTitle;
    if (this.isMulti) return localeStore.t('select_branches');
    return localeStore.t('select_branch');
  }

  @computed get selectedBranchesLength(): number {
    return this.isHighlightUnavailable
      ? this.selectedBranches.filter((branch) => !this.isUnavailable(branch)).length
      : this.selectedAvailableBranchesLength;
  }

  @computed get isNoBranchesForRender(): boolean {
    switch (true) {
      case !this.pagination.data.length:
      case this.isSelectedCrumb && !this.selectedBranches.length:
      case this.isSelectedCrumb && this.isHighlightUnavailable && !this.selectedAvailableBranchesLength:
        return true;
      default:
        return false;
    }
  }

  @computed get isIncludeFreezeChecker(): boolean {
    return Boolean(modalStore.branchesModal?.isFreezeOption);
  }

  private get selectedAvailableBranchesLength(): number {
    return this.selectedBranches.length - this.selectedUnavailableBranches.length;
  }

  private isAlreadySelect = (branch: IBranch): boolean => {
    return this.selectedBranches.some((_branch) => branch.id === _branch.id);
  };

  public isUnavailable = (branch: IBranch | IBranchesModalItem): boolean => {
    return getUnavailable(branch, modalStore.branchesModal?.unavailableFlag);
  };

  public isFreezeBranch = (branch: IBranch): boolean => {
    if (!this.isIncludeFreezeChecker) return false;
    return modalStore.branchesModal!.isFreezeOption!(branch);
  };

  private params = (selectedBranches?: boolean) => {
    const { fetchParamStatus, fetchParamDelete, profileNamesToShow } = modalStore.branchesModal || {};

    let status: TBooleanInNumber | undefined = undefined;
    let is_delete: TBooleanInNumber | undefined = undefined;
    let branches: number[] = [];
    const inc_profile_name: TBooleanInNumber | undefined = profileNamesToShow?.includes(BranchesModalProfile.DELIVERY) ? 1 : undefined;

    if (fetchParamStatus === BRANCHES_MODAL_FETCH_PARAMETER.INACTIVE) {
      status = 0;
    } else if (fetchParamStatus === BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE) {
      status = 1;
    }

    if (fetchParamDelete === BRANCHES_MODAL_FETCH_PARAMETER.INACTIVE) {
      is_delete = 1;
    } else if (fetchParamDelete === BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE) {
      is_delete = 0;
    }

    if (selectedBranches) {
      branches = this.selectedBranches.map((branch) => branch.id);
    } else if (fetchParamStatus === BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE) {
      branches = userStore.user.branches.filter((branch) => branch.status).map((_branch) => _branch.id);
    } else {
      branches = userStore.user.branches.map((branch) => branch.id);
    }

    return { status, is_delete, branches, name: this.searchValue, inc_profile_name };
  };

  public initStore = () => {
    this.loading = false;

    this.selectedBranches = toJS(modalStore.branchesModal?.selectedBranches) || [];
    this.selectedUnavailableBranches = modalStore.branchesModal?.checkUnavailable
      ? toJS(modalStore.branchesModal?.selectedBranches).filter((branch) => this.isUnavailable(branch))
      : [];
    this.askAgainBranches = [];

    this.searchValue = '';
    this.activeCrumb = MODALS_CRUMB.ALL;
    this.pagination = defaultPagination;

    this.fetchBranches(1);
  };

  @action fetchBranches = async (page: number = this.pagination.current_page + 1) => {
    if (this.isSelectedCrumb && !this.selectedBranchesLength) {
      this.pagination = defaultPagination;
      return;
    }

    try {
      this.loading = true;

      const pagination: IPagination<IBranch> = await client.get(`/v4/branches`, {
        params: { ...this.params(this.isSelectedCrumb), page: page === 0 ? 1 : page, per_page: PER_PAGE.DEFAULT },
      });

      this.pagination = pagination;
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'branchesModalStore fetchBranches' });
    }
  };

  @action changeCrumb = (crumb: MODALS_CRUMB) => {
    this.activeCrumb = crumb;
    this.fetchBranches(1);
  };

  @action onSearch = (search: string) => {
    this.searchValue = search;
    this.fetchBranches(1);
  };

  public askAgainHandler = (branch: IBranch, data: IAskAgainModalSubmit) => {
    if (!data.isConfirm) {
      this.selectedBranches = this.selectedBranches.filter((_branch) => _branch.id !== branch.id);
    }

    this.askAgainBranches = this.askAgainBranches.filter((_branch) => _branch.id !== branch.id);
  };

  private askAgainCheck = (branch: IBranch) => {
    if (modalStore.branchesModal?.askAgainChecker) {
      const needAsk: boolean = modalStore.branchesModal.askAgainChecker(branch);
      if (needAsk) this.askAgainBranches = this.askAgainBranches.concat(branch);
    }
  };

  @action public handlerSelectBranches = (branch: IBranch) => {
    const isValidExternal = modalStore.branchesModal?.shouldCheckPosConflict ? checkBranchesPosConflict(branch, this.selectedBranches) : true;

    const isFreezeBranch: boolean = this.isIncludeFreezeChecker && this.isFreezeBranch(branch);
    if (!isValidExternal || isFreezeBranch) return;

    if (!this.isAlreadySelect(branch)) this.askAgainCheck(branch);
    this.onBranchSelect(branch);
  };

  private onBranchSelect = (branch: IBranch) => {
    if (this.isAlreadySelect(branch)) {
      this.selectedBranches = this.selectedBranches.filter((_branch) => branch.id !== _branch.id);
      return;
    }

    if (this.isMulti) {
      this.selectedBranches = this.selectedBranches.concat(branch);
      return;
    }

    this.selectedBranches = [branch];
  };

  @action selectAll = async () => {
    if (this.isSelectedCrumb) return;

    try {
      this.loading = true;

      const pagination: IPagination<IBranch> = await client.get(`/v4/branches`, {
        params: { ...this.params(this.isSelectedCrumb), page: 1, per_page: PER_PAGE.MAXIMUM },
      });

      if (modalStore.branchesModal?.shouldCheckPosConflict) {
        pagination.data = getBranchesWithoutPosConflict(pagination.data, this.selectedBranches);
      }

      pagination.data.map((branch) => {
        if (this.isAlreadySelect(branch)) return;
        this.askAgainCheck(branch);
      });

      if (this.isIncludeFreezeChecker) {
        this.selectedBranches = arrayMerge(
          this.selectedBranches,
          pagination.data.filter((branch) => !this.isFreezeBranch(branch)),
          'id'
        );
      } else {
        this.selectedBranches = pagination.data;
      }

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'branchesModalStore selectAll' });
    }
  };

  @action deselectAll = () => {
    if (this.isIncludeFreezeChecker && this.selectedBranchesLength) {
      this.fetchDeselectBranches();
      return;
    }

    this.selectedBranches = [];
  };
  private fetchDeselectBranches = async () => {
    try {
      this.loading = true;

      const pagination: IPagination<IBranch> = await client.get(`/v4/branches`, {
        params: { ...this.params(true), status: undefined, is_delete: undefined, page: 1, per_page: PER_PAGE.MAXIMUM },
      });

      pagination.data.map((branch) => {
        if (this.isAlreadySelect(branch)) return;
        this.askAgainCheck(branch);
      });

      this.selectedBranches = pagination.data.filter((branch) => this.isFreezeBranch(branch));

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'branchesModalStore fetchDeselectBranches' });
    }
  };

  public onSave = () => {
    const isNoSelected: boolean = !modalStore.branchesModal?.saveWithoutBranches && !this.selectedBranches.length;

    if (!modalStore.branchesModal?.onSave || isNoSelected) {
      modalStore.closeBranchesModal();
      return;
    }

    modalStore.branchesModal.onSave(
      this.selectedBranches,
      this.pagination.data.filter((branch: IBranch) => this.selectedBranches.some((item) => item.id === branch?.id))
    );
  };
}

const branchesModalStore = new BranchesModalStore();
export default branchesModalStore;
